import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faPaperPlane, faMagic, faMobileAlt, faEnvelope, faTriangleExclamation, faDownload, faCopy } from '@fortawesome/free-solid-svg-icons';
import { CommunicationService, sharedApiClient } from '../../fetcher';
import { useNotifications, IdentityService } from '@medprompt/shared';
import { CustomDropdown } from '@medprompt/shared';
import { LmcContractViolationEnum } from '../../enums/lmcContractViolationEnum';
import '@medprompt/shared/src/common/modals.css';
import './MessagingModals.css';
import '../FileAwayModal.css';

// Modal modes
export const ModalMode = {
  PATIENT_MESSAGE: 'PATIENT_MESSAGE',
  CONTRACT_VIOLATION: 'CONTRACT_VIOLATION'
};

// Entity types
export const EntityType = {
  DOCUMENT: 'DOCUMENT',
  CONSULTATION: 'CONSULTATION'
};

const BaseMessageModal = ({ 
  mode = ModalMode.PATIENT_MESSAGE,
  entityType = EntityType.DOCUMENT,
  documentId, 
  consultationId,
  patientData, 
  doc, 
  consultation,
  onClose, 
  onMessageSent,
  templateRefreshTrigger = 0,
  autoOpenGenerateConfirmation = false,
}) => {
  // Determine the entity ID and entity object based on type
  const entityId = entityType === EntityType.CONSULTATION ? consultationId : documentId;
  const entity = entityType === EntityType.CONSULTATION ? consultation : doc;
  const [messageContent, setMessageContent] = useState('');
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedViolation, setSelectedViolation] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [clinicName, setClinicName] = useState('');
  const [isViolationDropdownOpen, setIsViolationDropdownOpen] = useState(false);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [patientCommTemplates, setPatientCommTemplates] = useState([]);
  const [customGreeting, setCustomGreeting] = useState('');
  const [isEditingGreeting, setIsEditingGreeting] = useState(false);
  const didAutoOpenGenerateConfirmRef = useRef(false);
  const { addNotification } = useNotifications();
  
  const communicationService = useMemo(() => new CommunicationService(sharedApiClient), []);
  const identityService = useMemo(() => new IdentityService(), []);
  const patient = patientData;

  const isPatientMode = mode === ModalMode.PATIENT_MESSAGE;
  const isContractMode = mode === ModalMode.CONTRACT_VIOLATION;

  useEffect(() => {
    if (!autoOpenGenerateConfirmation || !isPatientMode) {
      return;
    }
    if (didAutoOpenGenerateConfirmRef.current) {
      return;
    }
    didAutoOpenGenerateConfirmRef.current = true;
    setShowConfirmation(true);
  }, [autoOpenGenerateConfirmation, isPatientMode]);

  // Session storage keys
  const getStorageKey = () => {
    const prefix = entityType === EntityType.CONSULTATION ? 'consultation' : 'document';
    if (isPatientMode) {
      return `medprompt_patient_message_${prefix}_${entityId}`;
    } else if (isContractMode) {
      return `medprompt_contract_violation_${prefix}_${entityId}`;
    }
    return null;
  };

  // Load draft from session storage on mount
  useEffect(() => {
    const storageKey = getStorageKey();
    if (storageKey) {
      try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          const draft = JSON.parse(saved);
          if (draft.content) {
            setMessageContent(draft.content);
          }
          // For contract mode, also restore violation selection
          if (isContractMode && draft.violation) {
            setSelectedViolation(draft.violation);
          }
          // For patient mode, restore custom greeting if available
          if (isPatientMode && draft.greeting) {
            setCustomGreeting(draft.greeting);
          }
        }
      } catch (error) {
        console.error('Failed to load draft from session storage:', error);
      }
    }
  }, [entityId, mode, entityType]);

  // Fetch clinic name on mount
  useEffect(() => {
    const fetchClinicName = async () => {
      try {
        const clinics = await identityService.fetchClinics();
        const selectedId = sessionStorage.getItem('selectedClinicId');
        const clinic = clinics?.find(c => c.clinicId === selectedId) || clinics?.[0];
        setClinicName(clinic?.clinicName || '');
      } catch (error) {
        console.error('Failed to fetch clinic:', error);
      }
    };
    fetchClinicName();
  }, []);

  // Save draft to session storage whenever content or violation changes
  useEffect(() => {
    const storageKey = getStorageKey();
    if (storageKey) {
      try {
        const draft = {
          content: messageContent
        };
        // For contract mode, also save violation selection
        if (isContractMode) {
          draft.violation = selectedViolation;
        }
        // For patient mode, save custom greeting
        if (isPatientMode && customGreeting) {
          draft.greeting = customGreeting;
        }
        sessionStorage.setItem(storageKey, JSON.stringify(draft));
      } catch (error) {
        console.error('Failed to save draft to session storage:', error);
      }
    }
  }, [messageContent, selectedViolation, customGreeting, entityId, mode, entityType]);

  // Fetch templates on mount and when refresh trigger changes
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templates = await communicationService.fetchCommTemplates();
        setPatientCommTemplates(templates || []);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        setPatientCommTemplates([]);
      }
    };
    
    if (isPatientMode) {
      fetchTemplates();
    }
  }, [templateRefreshTrigger, isPatientMode]);

  // Get all contract violations from enum and mark which are flagged on the entity.
  // Flagged ones stay in enum order, then the rest, so the dropdown leads with this document's violations.
  const flaggedViolations = entity?.lmcContractViolations || [];
  const violationOptions = Object.keys(LmcContractViolationEnum).map(violationKey => {
    const violationData = LmcContractViolationEnum[violationKey];
    const isFlagged = flaggedViolations.includes(violationKey);
    return {
      value: violationKey,
      display: violationData?.displayName || violationKey,
      description: violationData?.description || '',
      isBold: isFlagged,
      icon: isFlagged ? faTriangleExclamation : null
    };
  }).sort((a, b) => Number(b.isBold) - Number(a.isBold));

  // Determine available send methods (only for patient mode)
  const availableMethods = isPatientMode ? {
    text: !!patient?.phoneNumber,
    email: !!patient?.email
  } : {};

  // Get patient salutation based on title or fallback to sex
  const getSalutation = () => {
    if (!isPatientMode) return '';
    
    // Use the title if available
    if (patient?.title) {
      return patient.title;
    }
    
    // Fallback to inferring from sex if no title is provided
    const sex = patient?.sex?.toLowerCase();
    if (sex === 'male' || sex === 'm') return 'Mr';
    if (sex === 'female' || sex === 'f') return 'Mrs';
    return 'Mr/Mrs';
  };

  // Get patient surname (last word in name)
  const getSurname = () => {
    if (!isPatientMode) return '';
    const name = patient?.name || '';
    const parts = name.trim().split(/\s+/);
    return parts.length > 0 ? parts[parts.length - 1] : '';
  };

  // Initialize custom greeting with default if not set
  useEffect(() => {
    if (isPatientMode && !customGreeting && patient) {
      const defaultGreeting = `Dear ${getSalutation()} ${getSurname()},`;
      setCustomGreeting(defaultGreeting);
    }
  }, [patient, isPatientMode]);

  // Construct greeting and signature
  const greeting = isPatientMode ? (customGreeting ? `${customGreeting}\n\n` : `Dear ${getSalutation()} ${getSurname()},\n\n`) : '';
  const signature = `\n\nKind regards,\n${clinicName || 'Your Practice'}`

  // Handlers for greeting editing
  const handleGreetingClick = () => {
    setIsEditingGreeting(true);
  };

  const handleGreetingBlur = () => {
    setIsEditingGreeting(false);
    // Trim the greeting to remove extra whitespace
    if (customGreeting) {
      setCustomGreeting(customGreeting.trim());
    }
  };

  const handleGreetingKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsEditingGreeting(false);
      // Reset to default if empty
      if (!customGreeting.trim()) {
        const defaultGreeting = `Dear ${getSalutation()} ${getSurname()},`;
        setCustomGreeting(defaultGreeting);
      }
    }
  };

  // Get selected patient note tasks (only for patient mode)
  const getSelectedPatientTasks = () => {
    if (!isPatientMode || !entity?.outcomeTasks) return [];
    return entity.outcomeTasks.filter(
      task => task.isSelected && task.taskType === 'PATIENT_NOTE'
    );
  };

  const handleGenerate = async () => {
    if (isPatientMode) {
      // Always show confirmation modal to review patient tasks
      setShowConfirmation(true);
    } else if (isContractMode) {
      // For contract mode, check if violation is selected
      if (!selectedViolation) {
        addNotification('violation-error', 'Please select a contract violation', { isError: true });
        return;
      }
      performContractGeneration();
    }
  };

  const performGeneration = async () => {
    setIsGenerating(true);
    setShowConfirmation(false);
    
    try {
      let response;
      if (entityType === EntityType.CONSULTATION) {
        response = await communicationService.generateConsultationMessageContent(entityId);
      } else {
        response = await communicationService.generateMessageContent(entityId);
      }
      setMessageContent(response.content || '');
      addNotification('generate-success', 'Message content generated successfully', { isError: false });
    } catch (error) {
      console.error('Failed to generate message:', error);
      addNotification('generate-error', 'Failed to generate message content', { isError: true });
    } finally {
      setIsGenerating(false);
    }
  };

  const performContractGeneration = async () => {
    setIsGenerating(true);
    
    try {
      // Contract violations are only supported for documents
      const response = await communicationService.generateContractViolationLetter(documentId, selectedViolation);
      setMessageContent(response.content || '');
      addNotification('generate-success', 'Contract violation letter generated successfully', { isError: false });
    } catch (error) {
      console.error('Failed to generate contract violation letter:', error);
      addNotification('generate-error', 'Failed to generate contract violation letter', { isError: true });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!messageContent.trim()) {
      addNotification('send-error', 'Please enter a message', { isError: true });
      return;
    }

    if (selectedMethods.length === 0) {
      addNotification('send-error', 'Please select at least one send method', { isError: true });
      return;
    }

    setIsSending(true);

    try {
      // Build the types list based on selected methods
      const types = selectedMethods.map(method => method.toUpperCase());

      // Combine greeting, message content, and signature
      const fullMessage = greeting + messageContent + signature;

      const commRequest = {
        types,
        content: fullMessage
      };

      if (entityType === EntityType.CONSULTATION) {
        await communicationService.sendConsultationMessage(entityId, commRequest);
      } else {
        await communicationService.sendMessage(entityId, commRequest);
      }
      addNotification('send-success', 'Message sent successfully', { isError: false });
      
      // Clear draft from session storage after successful send
      const storageKey = getStorageKey();
      if (storageKey) {
        try {
          sessionStorage.removeItem(storageKey);
        } catch (error) {
          console.error('Failed to clear draft from session storage:', error);
        }
      }
      
      // Call callback if provided
      if (onMessageSent) {
        onMessageSent();
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to send message:', error);
      addNotification('send-error', 'Failed to send message', { isError: true });
    } finally {
      setIsSending(false);
    }
  };

  const handleMethodToggle = (method) => {
    if (!availableMethods[method]) return;
    
    setSelectedMethods(prev => {
      if (prev.includes(method)) {
        return prev.filter(m => m !== method);
      } else {
        return [...prev, method];
      }
    });
  };

  const handleCopyToClipboard = () => {
    const fullContent = greeting + messageContent + signature;
    
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(fullContent).then(() => {
        addNotification('copy-success', 'Copied to clipboard', { isError: false });
      }).catch(() => {
        addNotification('copy-error', 'Failed to copy to clipboard', { isError: true });
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullContent;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        addNotification('copy-success', 'Copied to clipboard', { isError: false });
      } catch (err) {
        addNotification('copy-error', 'Failed to copy to clipboard', { isError: true });
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadPDF = () => {
    try {
      const fullContent = greeting + messageContent + signature;
      const dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      const violationLabel = isContractMode && selectedViolation
        ? (LmcContractViolationEnum[selectedViolation]?.displayName || selectedViolation)
        : null;

      // Escape HTML entities so raw content is safe inside a <pre>
      const escapeHtml = (str) =>
        str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${isPatientMode ? 'Patient Message' : 'Transfer of Care Violation Letter'}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 12pt;
      line-height: 1.7;
      color: #111;
      background: #fff;
    }
    .page {
      max-width: 700px;
      margin: 60px auto;
      padding: 0 40px;
    }
    .letter-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 40px;
      padding-bottom: 16px;
      border-bottom: 1px solid #ccc;
    }
    .clinic-name {
      font-size: 16pt;
      font-weight: bold;
      letter-spacing: 0.5px;
    }
    .letter-meta {
      text-align: right;
      font-size: 10pt;
      color: #444;
      line-height: 1.6;
    }
    .violation-badge {
      display: inline-block;
      margin-bottom: 28px;
      padding: 4px 12px;
      background: #fef2f2;
      border: 1px solid #fca5a5;
      border-radius: 4px;
      font-size: 10pt;
      color: #b91c1c;
      font-family: Arial, sans-serif;
    }
    .letter-body {
      white-space: pre-wrap;
      word-break: break-word;
      font-size: 12pt;
      line-height: 1.8;
    }
    @media print {
      body { margin: 0; }
      .page { margin: 0; padding: 20mm 20mm; max-width: 100%; }
      @page { margin: 15mm; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="letter-header">
      <div class="clinic-name">${escapeHtml(clinicName || 'Your Practice')}</div>
      <div class="letter-meta">
        <div>${dateStr}</div>
        ${isPatientMode && patient?.name ? `<div>${escapeHtml(patient.name)}</div>` : ''}
      </div>
    </div>
    ${violationLabel ? `<div class="violation-badge">Re: ${escapeHtml(violationLabel)}</div>` : ''}
    <div class="letter-body">${escapeHtml(fullContent)}</div>
  </div>
  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        addNotification('pdf-error', 'Pop-up blocked — please allow pop-ups for this site', { isError: true });
        return;
      }
      printWindow.document.write(html);
      printWindow.document.close();

      addNotification('pdf-success', 'Print dialog opened — choose "Save as PDF"', { isError: false });
    } catch (error) {
      console.error('Failed to open print view:', error);
      addNotification('pdf-error', 'Failed to open print view', { isError: true });
    }
  };

  const handleViolationSelect = (violation) => {
    setSelectedViolation(violation.value);
    setIsViolationDropdownOpen(false);
  };

  const getSelectedViolationDisplay = () => {
    if (!selectedViolation) return 'Select a contract violation...';
    const violation = LmcContractViolationEnum[selectedViolation];
    return violation?.displayName || selectedViolation;
  };

  // Template dropdown handlers
  const handleTemplateSelect = (template) => {
    setMessageContent(template.content);
    setSelectedTemplate(template);
    setIsTemplateDropdownOpen(false);
  };

  const getSelectedTemplateDisplay = () => {
    if (!selectedTemplate) return 'Select a template...';
    return selectedTemplate.title;
  };

  // Create template options for dropdown
  const templateOptions = patientCommTemplates.map((template, index) => ({
    id: index,
    display: template.title,
    value: template
  }));

  const hasTemplates = patientCommTemplates && patientCommTemplates.length > 0;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container create-message-modal modal-animated" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{isPatientMode ? 'Send Message to Patient' : 'Write Transfer of Care Violation Letter'}</h2>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="modal-content create-message-content">
            {/* Patient Info - only show in patient mode */}
            {isPatientMode && (
              <div className="patient-info-section">
                <div className="patient-name">{patient?.name || 'Unknown Patient'}</div>
                <div className="patient-contact-info">
                  {patient?.phoneNumber && (
                    <div className="contact-info-item">
                      <FontAwesomeIcon icon={faMobileAlt} />
                      <span>{patient.phoneNumber}</span>
                    </div>
                  )}
                  {patient?.email && (
                    <div className="contact-info-item">
                      <FontAwesomeIcon icon={faEnvelope} />
                      <span>{patient.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contract Violation Selection - only show in contract mode */}
            {isContractMode && (
              <div className="send-method-section">
                <label className="section-label">Contract Violation:</label>
                <CustomDropdown
                  selected={getSelectedViolationDisplay()}
                  options={violationOptions}
                  isOpen={isViolationDropdownOpen}
                  toggleDropdown={() => setIsViolationDropdownOpen(!isViolationDropdownOpen)}
                  onSelect={handleViolationSelect}
                  className="send-message-violation-dropdown"
                />
              </div>
            )}

            {/* Send Method Selection - only show in patient mode */}
            {isPatientMode && (
              <div className="send-method-section">
                <label className="section-label">Send via (select one or more):</label>
                <div className="method-options">
                  <button
                    className={`method-button ${selectedMethods.includes('text') ? 'selected' : ''} ${!availableMethods.text ? 'disabled' : ''}`}
                    onClick={() => handleMethodToggle('text')}
                    disabled={!availableMethods.text}
                    title={!availableMethods.text ? 'No phone number available' : 'Send via SMS'}
                  >
                    SMS
                  </button>
                  <button
                    className={`method-button ${selectedMethods.includes('email') ? 'selected' : ''} ${!availableMethods.email ? 'disabled' : ''}`}
                    onClick={() => handleMethodToggle('email')}
                    disabled={!availableMethods.email}
                    title={!availableMethods.email ? 'No email address available' : 'Send via Email'}
                  >
                    Email
                  </button>
                </div>
              </div>
            )}

            {/* Message Content */}
            <div className="message-content-section">
              <div className="message-header-row">
                <label className="section-label">{isPatientMode ? 'Message' : 'Letter Content'}</label>
                <div className="message-action-buttons">
                  {isPatientMode && (
                    <CustomDropdown
                      selected={getSelectedTemplateDisplay()}
                      options={templateOptions}
                      isOpen={isTemplateDropdownOpen}
                      toggleDropdown={() => setIsTemplateDropdownOpen(!isTemplateDropdownOpen)}
                      onSelect={(option) => handleTemplateSelect(option.value)}
                      className="template-dropdown"
                      disabled={!hasTemplates}
                      width="200px"
                    />
                  )}
                  <button
                    className={`generate-button ${isGenerating ? 'generating' : ''}`}
                    onClick={handleGenerate}
                    disabled={isGenerating || (isContractMode && !selectedViolation)}
                    title={isPatientMode ? "Generate message content with AI" : "Generate contract violation letter with AI"}
                  >
                    {isGenerating ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faMagic} />
                        <span>Generate Content</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
                <div className="message-compose-container">
                {isPatientMode && (
                  <div className="message-greeting-section">
                    {isEditingGreeting ? (
                      <input
                        type="text"
                        className="greeting-input-editable"
                        value={customGreeting}
                        onChange={(e) => setCustomGreeting(e.target.value)}
                        onBlur={handleGreetingBlur}
                        onKeyDown={handleGreetingKeyDown}
                        autoFocus
                        placeholder="Enter greeting..."
                      />
                    ) : (
                      <div 
                        className="greeting-text-editable"
                        onClick={handleGreetingClick}
                        onMouseMove={(e) => {
                          const tooltip = e.currentTarget.querySelector('.greeting-edit-tooltip');
                          if (tooltip) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const offsetX = e.clientX - rect.left;
                            const offsetY = e.clientY - rect.top;
                            tooltip.style.left = `${offsetX + 15}px`;
                            tooltip.style.top = `${offsetY + 20}px`;
                          }
                        }}
                      >
                        {customGreeting || `Dear ${getSalutation()} ${getSurname()},`}
                        <div className="greeting-edit-tooltip">Click to edit greeting</div>
                      </div>
                    )}
                  </div>
                )}
                <textarea
                  className="message-textarea-editable"
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder={isPatientMode ? "Enter your message here..." : "Enter letter content here..."}
                  disabled={isGenerating}
                  rows={isPatientMode ? 6 : 10}
                />
                {(isPatientMode || isContractMode) && (
                  <div className="message-frozen-section message-signature">
                    {signature}
                  </div>
                )}
              </div>
              <div className="character-count">
                {(greeting + messageContent + signature).length} characters
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose} disabled={isSending || isGenerating}>
              Cancel
            </button>
            
            {isContractMode && (
              <>
                <button 
                  className="send-message-copy-button" 
                  onClick={handleCopyToClipboard}
                  disabled={!messageContent.trim()}
                  title="Copy to clipboard"
                >
                  <FontAwesomeIcon icon={faCopy} />
                  <span>Copy</span>
                </button>
                <button 
                  className="send-message-download-button" 
                  onClick={handleDownloadPDF}
                  disabled={!messageContent.trim()}
                  title="Download as PDF"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Download PDF</span>
                </button>
              </>
            )}
            
            {isPatientMode && (
              <button 
                className="send-button" 
                onClick={handleSend}
                disabled={isSending || isGenerating || !messageContent.trim() || selectedMethods.length === 0}
              >
                {isSending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} />
                    <span>Send</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Generation - only for patient mode */}
      {showConfirmation && isPatientMode && (() => {
        const selectedPatientTasks = getSelectedPatientTasks();
        const hasContent = messageContent.trim().length > 0;
        
        return (
          <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
            <div className="modal-container confirmation-modal modal-animated" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Generate Message Content</h2>
                <button className="close-button" onClick={() => setShowConfirmation(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className="modal-content">
                <p className="fileaway-intro">
                  The following patient tasks will be used to generate message content:
                </p>
                
                {/* Warning: No Patient Tasks Selected */}
                {selectedPatientTasks.length === 0 && (
                  <div className="fileaway-document-warning">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    No patient message tasks have been selected
                  </div>
                )}
                
                {/* Warning: Content Will Be Overwritten */}
                {hasContent && (
                  <div className="fileaway-document-warning">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    Generating new content will overwrite your current message
                  </div>
                )}
                
                {/* Selected Patient Tasks */}
                <div className="fileaway-section">
                  <h3>Selected Patient Tasks</h3>
                  {selectedPatientTasks.length === 0 ? (
                    <div className="fileaway-none">None selected</div>
                  ) : (
                    <div className="fileaway-items">
                      {selectedPatientTasks.map(task => (
                        <div key={task.taskId} className="selected-task-item">
                          <div className="task-main-content">
                            <span className="code-term">{task.text}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button className="cancel-button" onClick={() => setShowConfirmation(false)}>
                  Cancel
                </button>
                <button className="submit-button" onClick={performGeneration}>
                  Generate
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default React.memo(BaseMessageModal);
